import { useEffect, useState } from "react";
import { Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (e) {
    console.warn("[reviews] could not format date", s, e);
    return "";
  }
}

export const ReviewSection = ({ skillId, onChanged }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], my_review: null, can_review: false });
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/skills/${skillId}/reviews`);
      setData(res.data);
      if (res.data.my_review) {
        setRating(res.data.my_review.rating);
        setComment(res.data.my_review.comment || "");
      }
    } catch (e) {
      console.error("[reviews] failed to load reviews", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillId]);

  const submit = async () => {
    if (!rating) {
      toast.error("Please pick a star rating");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/skills/${skillId}/reviews`, { rating, comment });
      toast.success(data.my_review ? "Review updated" : "Review submitted");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(apiErr(e, "Could not submit review"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-section">
      <div className="mb-4 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-[color:var(--rb-accent)]" />
        <h3 className="rb-display text-lg font-semibold text-[color:var(--rb-text)]">Reviews</h3>
        <Badge variant="outline" className="border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]">{data.reviews.length}</Badge>
      </div>

      {data.can_review && (
        <div className="mb-6 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-4" data-testid="review-form">
          <p className="mb-2 text-sm font-medium text-[color:var(--rb-text)]">{data.my_review ? "Edit your review" : "Rate this skill"}</p>
          <StarRating rating={rating} interactive onChange={setRating} showNumber={false} size={22} />
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share what worked well (optional)"
            data-testid="review-comment-input"
            className="mt-3 min-h-[80px] border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-sm text-[color:var(--rb-text)]"
          />
          <Button onClick={submit} disabled={submitting} data-testid="submit-review-button"
            className="mt-3 rounded-xl bg-[color:var(--rb-accent)] text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : data.my_review ? "Update review" : "Submit review"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8 text-[color:var(--rb-text-faint)]"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : data.reviews.length === 0 ? (
        <p className="py-6 text-sm text-[color:var(--rb-text-faint)]">No reviews yet. Verified buyers can be the first to review.</p>
      ) : (
        <div className="space-y-4">
          {data.reviews.map((r) => (
            <div key={r.id} className="rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4" data-testid="review-item">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={r.reviewer_avatar} />
                    <AvatarFallback className="bg-[color:var(--rb-surface)] text-[10px] text-[color:var(--rb-accent)]">{(r.reviewer_name || "U").slice(0, 1).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2 text-sm text-[color:var(--rb-text)]">
                      {r.reviewer_name ? `@${r.reviewer_name}` : "Anonymous"}
                      {user && r.reviewer_id === user.user_id && <Badge variant="outline" className="border-[color:var(--rb-border)] text-[10px] text-[color:var(--rb-text-faint)]">You</Badge>}
                    </div>
                    <div className="text-xs text-[color:var(--rb-text-faint)]">{fmtDate(r.updated_at || r.created_at)}</div>
                  </div>
                </div>
                <StarRating rating={r.rating} showNumber={false} size={14} />
              </div>
              {r.comment && <p className="mt-3 text-sm leading-relaxed text-[color:var(--rb-text-dim)]">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
